import { GatewayStatus } from 'constants/gatewayStatuses';
import { MethodTypes } from 'constants/methodTypes';
import { OperationAccess } from 'constants/operationAccesses';
import { Resources } from 'constants/resources';

import { useNormalizeReferences } from 'hooks/useNormalizeReferences';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { GatewaysChip } from 'pages/Gateways/components/GatewaysChip/GatewaysChip';
import { BooleanInputStyled, BoxStyled } from 'pages/Gateways/components/GatewaysForm.styled';
import { GatewaysFormSkeleton } from 'pages/Gateways/components/GatewaysFormSkeleton';
import { GatewayStatusSelectInput } from 'pages/Gateways/components/GatewayStatusSelectInput/GatewayStatusSelectInput';
import { useGatewayProviderAutofill } from 'pages/Gateways/hooks/useGatewayProviderAutofill';
import { useRecordContext } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { CancelButton, SaveButton, DeleteButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { Stack } from 'shared/mui/Stack/Stack';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  NumberInput,
  SelectInput,
  TextInput,
} from 'shared/react-admin/Inputs';
import { GenerateNameInput } from 'shared/react-admin/Inputs/TextInputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Currency } from 'types/embedded/currency';
import { Permission } from 'types/embedded/permission';
import { getId } from 'utils';

import { GatewaysEditMethodsAutocomplete } from './components/GatewaysEditMethodsAutocomplete/GatewaysEditMethodsAutocomplete';
import { GatewaysEditPilotSettings } from './components/GatewaysEditPilotSettings/GatewaysEditPilotSettings';
import { GatewaysEditFormDeleteDialog } from './GatewaysEditFormDeleteDialog';
import { useGatewayMethodsFieldArray } from './hooks/useGatewayMethodsFieldArray';
import { GatewayEditFormValues } from './types';

export const GatewaysEditForm: React.FC = () => {
  const record = useRecordContext<GatewayEditFormValues>();
  const { hasResourcePermissions, hasOperationAccess } = usePermissions();
  const { control, setValue, watch } = useFormContext();
  const { handleOpenRecordModal, handleCloseRecordModal, openRecordModal } =
    useRecordModal<GatewayEditFormValues>();

  const { gatewayMethodFields, onAddGatewayMethod, onRemoveGatewayMethod } =
    useGatewayMethodsFieldArray();

  const { fillProviderByGeneratedName, providerFilter, resetProviderFilter } =
    useGatewayProviderAutofill();

  const { ready } = useNormalizeReferences({
    referenceFields: [
      'provider',
      'direction',
      'defaultCurrency',
      'supportedCurrencies',
      'status',
      'deactivationReason',
    ],
  });

  const watchStatus = useWatch({ control, name: 'status' });
  const watchPilotModeSchedule = useWatch({ control, name: 'gatewayPilotModeSchedule.schedule' });
  const statusId = getId(watchStatus);

  const canViewCurrencies = hasResourcePermissions(Resources.Currencies, [
    Permission.Full,
    Permission.Read,
  ]);

  const canViewProviders = hasResourcePermissions(Resources.Providers, [
    Permission.Full,
    Permission.Read,
  ]);

  const isPilotGatewayStatus = statusId === GatewayStatus.Pilot;
  const isDisabledGatewayStatus = statusId === GatewayStatus.Disabled;

  if (!ready) {
    return <GatewaysFormSkeleton />;
  }

  return (
    <>
      <Box pt="24px">
        <BoxStyled>
          <GenerateNameInput
            aliasSource="externalAlias"
            fullWidth
            isRequired
            label="Название"
            onGenerate={canViewProviders ? fillProviderByGeneratedName : undefined}
            size="small"
            source="name"
            variant="outlined"
          />
          <TextInput
            fullWidth
            isRequired
            label="Внешний алиас"
            size="small"
            source="externalAlias"
            variant="outlined"
          />
          <TextInput
            fullWidth
            isRequired
            label="Алиас"
            size="small"
            source="alias"
            variant="outlined"
          />
          <GatewayStatusSelectInput />
        </BoxStyled>
        {isPilotGatewayStatus && (
          <GatewaysEditPilotSettings
            gatewayMethodFields={gatewayMethodFields}
            isPilotModeSchedule={watchPilotModeSchedule}
          />
        )}
        <BoxStyled>
          {canViewProviders && (
            <ReferenceInput
              filter={providerFilter}
              perPage={200}
              reference={Resources.Providers}
              source="provider"
            >
              <AutocompleteInput
                filterToQuery={(searchText: string) => ({ name: searchText })}
                fullWidth
                isRequired
                label="Провайдер"
                onInputChange={resetProviderFilter}
                optionText="name"
                optionValue="@id"
                size="small"
                variant="outlined"
              />
            </ReferenceInput>
          )}
          <ReferenceInput reference={Resources.GatewayDirections} source="direction">
            <SelectInput
              fullWidth
              isRequired
              label="Направление"
              optionText={<GatewaysChip />}
              optionValue="@id"
              size="small"
              source="value"
              variant="outlined"
            />
          </ReferenceInput>
          {isDisabledGatewayStatus && (
            <ReferenceInput
              perPage={200}
              reference={Resources.GatewayDeactivationReasons}
              source="deactivationReason"
            >
              <AutocompleteInput
                filterToQuery={(searchText: string) => ({ name: searchText })}
                fullWidth
                isRequired
                label="Причина деактивации"
                optionText="reason"
                size="small"
                variant="outlined"
              />
            </ReferenceInput>
          )}
        </BoxStyled>
        <BoxStyled>
          <BooleanInputStyled
            disabled
            label="Автосоздание метода"
            size="small"
            source="autocreateMethod"
            sx={{ alignSelf: 'center' }}
          />
          <ReferenceArrayInput
            filter={
              watch('pcidss')
                ? {
                    methodType: {
                      alias: MethodTypes.PCIDSSBankCard,
                    },
                  }
                : {}
            }
            reference={Resources.Methods}
            source="method"
          >
            <GatewaysEditMethodsAutocomplete
              onAddGatewayMethod={onAddGatewayMethod}
              onRemoveGatewayMethod={onRemoveGatewayMethod}
            />
          </ReferenceArrayInput>
          {canViewCurrencies && (
            <ReferenceInput
              filter={{ active: true }}
              perPage={1000}
              reference={Resources.Currencies}
              sort={{ field: 'id', order: 'ASC' }}
              source="defaultCurrency"
            >
              <AutocompleteInput
                filterToQuery={(searchText: string) => ({ currency: searchText })}
                fullWidth
                isRequired
                label="Валюта по умолчанию"
                matchSuggestion={(filter, choice) => {
                  return choice.name.match(filter);
                }}
                optionText={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
                optionValue="@id"
                size="small"
                variant="outlined"
              />
            </ReferenceInput>
          )}
          {canViewCurrencies && (
            <ReferenceArrayInput
              filter={{ active: true }}
              perPage={1000}
              reference={Resources.Currencies}
              sort={{ field: 'id', order: 'ASC' }}
              source="supportedCurrencies"
            >
              <AutocompleteMultiselectInput
                clearOnBlur={false}
                filterToQuery={(searchText: string) => ({ currency: searchText })}
                fullWidth
                getOptionLabel={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
                label="Поддерживаемые валюты"
                name="supportedCurrencies"
                optionValue="@id"
                size="small"
              />
            </ReferenceArrayInput>
          )}
        </BoxStyled>
        <BoxStyled>
          <BooleanInputStyled
            label="Опрос статуса заказа"
            size="small"
            source="statusRequestEnabled"
            sx={{ alignSelf: 'center' }}
          />
          <NumberInput
            defaultValue={18}
            format={(value) => value || 0}
            fullWidth
            label="Макс. количество попыток опроса"
            parse={(value) => value || 0}
            size="small"
            source="transactionRequestMaxAttempts"
            variant="outlined"
          />
          <TextInput
            defaultValue="5, 10, 15, 20, 40, 60, 120"
            fullWidth
            isRequired
            label="Интервал опроса статуса"
            size="small"
            source="attemptIntervalConfig"
            variant="outlined"
          />
          <NumberInput
            format={(value) => value || 0}
            fullWidth
            isRequired
            label="Срок действия заказа (сек)"
            parse={(value) => value || 0}
            size="small"
            source="orderExpirationTimeSec"
            variant="outlined"
          />
        </BoxStyled>
        <Stack direction="row" gap={3}>
          {hasOperationAccess(OperationAccess.ManageMerchantAccounts) && (
            <Box>
              <BooleanInputStyled
                label="Проверен менеджером"
                size="small"
                source="managerVerified"
              />
            </Box>
          )}
          <BooleanInputStyled
            label="PCI DSS"
            onChange={() => {
              setValue('gatewayMethods', []);
              setValue('method', []);
            }}
            size="small"
            source="pcidss"
          />
          <BooleanInputStyled label="Push API" size="small" source="pushApi" />
          <BooleanInputStyled label="Требуется сумма" size="small" source="requireAmount" />
          <BooleanInputStyled
            label="Сохранить данные метода"
            size="small"
            source="saveCustomerMethodData"
          />
        </Stack>
      </Box>
      <CardActionsButtons
        leftActionsSlot={
          hasResourcePermissions(Resources.Gateways, [Permission.Full, Permission.Delete]) && (
            <DeleteButton onClick={() => handleOpenRecordModal(record)} variant="outlined" />
          )
        }
        rightActionsSlot={[
          <CancelButton key="cancel-button" linkType="back" variant="outlined" />,
          <SaveButton key="save-button" label="Сохранить" />,
        ]}
        sx={{ marginTop: 'auto', padding: 0 }}
      />
      {record && (
        <GatewaysEditFormDeleteDialog
          handleCloseRecordModal={handleCloseRecordModal}
          openRecordModal={openRecordModal}
          record={record}
        />
      )}
    </>
  );
};
